import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { apiGet } from '../services/api';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import RefreshButton from '../components/RefreshButton';
import { KpiCard, SectionCard, monthLabel, pct, rateColor } from '../components/PainelDiarioUI';

/**
 * Painel do professor — a coordenação acompanha o que ele lançou.
 *
 * Lê /teachers/<id>/acompanhamento/, que sai do mesmo cálculo do
 * Acompanhamento de Diários (aulas previstas pela grade x registradas), só
 * que pela pessoa e não pela turma. Seis blocos: os números do período, por
 * turma e disciplina, as aulas pendentes, o período dia a dia (ou mês a mês),
 * notas/habilidades por bimestre e a última atividade.
 */

const STATUS_META = {
  ok: { label: 'Completo', dot: 'bg-[#13983D]', text: 'text-[#13983D]', soft: 'bg-[#13983D]/10' },
  substituicao: { label: 'Substituição', dot: 'bg-[#7C3AED]', text: 'text-[#7C3AED]', soft: 'bg-[#7C3AED]/10' },
  sem_frequencia: { label: 'Sem chamada', dot: 'bg-[#F9B047]', text: 'text-[#B87608]', soft: 'bg-[#F9B047]/15' },
  pendente: { label: 'Não registrada', dot: 'bg-[#E83A3C]', text: 'text-[#E83A3C]', soft: 'bg-[#E83A3C]/10' },
  nao_realizada: { label: 'Não realizada', dot: 'bg-slate-500', text: 'text-slate-500', soft: 'bg-slate-500/10' },
  extra: { label: 'Aula extra', dot: 'bg-[#0974BA]', text: 'text-[#0974BA]', soft: 'bg-[#0974BA]/10' },
};

const DIAS_DA_SEMANA = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

const mesAtual = () => new Date().toISOString().slice(0, 7);

/** 'AAAA-MM-DD' → 'dd/mm'. */
const diaMes = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—');

/** Data e hora ISO → 'dd/mm/aaaa'. */
const dataCurta = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—');

/** Há quantos dias, em texto: 'hoje', 'ontem', 'há 5 dias'. */
function haQuanto(iso) {
  if (!iso) return '';
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (dias <= 0) return 'hoje';
  if (dias === 1) return 'ontem';
  return `há ${dias} dias`;
}

/** Situação de um dia pela pior aula dele, para a cor do quadradinho. */
function situacaoDoDia(dia) {
  if (dia.pendente) return 'pendente';
  if (dia.sem_frequencia) return 'sem_frequencia';
  if (dia.ok || dia.substituicao || dia.extra) return 'ok';
  if (dia.nao_realizada) return 'nao_realizada';
  return null;
}

function corDoPct(valor) {
  if (valor === null || valor === undefined) return 'text-slate-400';
  if (valor >= 100) return 'text-[#13983D]';
  if (valor > 0) return 'text-[#B87608]';
  return 'text-[#E83A3C]';
}

function StatusPill({ status }) {
  const meta = STATUS_META[status];
  if (!meta) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${meta.soft} ${meta.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}

/** Mês: calendário com a situação de cada dia. */
function CalendarioDoMes({ dias, darkMode }) {
  if (!dias.length) return null;
  // Segunda = 0: o calendário começa na segunda, como a grade.
  const primeiro = (new Date(`${dias[0].date}T12:00:00`).getDay() + 6) % 7;
  const vazios = Array.from({ length: primeiro });
  return (
    <div>
      <div className="grid grid-cols-7 gap-1.5 mb-1.5">
        {DIAS_DA_SEMANA.map((d) => (
          <div key={d} className={`text-[11px] text-center font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {vazios.map((_, i) => <div key={`v${i}`} />)}
        {dias.map((dia) => {
          const situacao = situacaoDoDia(dia);
          const meta = situacao && STATUS_META[situacao];
          const previstas = dia.esperadas;
          return (
            <div
              key={dia.date}
              title={`${diaMes(dia.date)}: ${dia.lancadas} lançada(s), ${dia.sem_frequencia} sem chamada, ${dia.pendente} não registrada(s)${dia.futura ? `, ${dia.futura} futura(s)` : ''}`}
              className={`rounded-lg p-1.5 min-h-[52px] flex flex-col justify-between border ${meta
                ? `${meta.soft} border-transparent`
                : darkMode ? 'border-slate-700/60' : 'border-slate-100'}`}
            >
              <span className={`text-[11px] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{Number(dia.date.slice(8, 10))}</span>
              {previstas > 0 || dia.extra > 0 ? (
                <span className={`text-xs font-semibold tabular-nums ${meta ? meta.text : ''}`}>
                  {dia.lancadas}/{previstas}
                </span>
              ) : dia.futura > 0 ? (
                <span className="text-[11px] text-slate-400">{dia.futura} prev.</span>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Ano: uma barra por mês, lançadas x sem chamada x não registradas. */
function BarrasDoAno({ meses, darkMode }) {
  const maior = Math.max(1, ...meses.map((m) => m.esperadas || 0));
  return (
    <div className="flex items-end gap-2 h-44">
      {meses.map((m) => {
        const altura = (n) => `${((n || 0) / maior) * 100}%`;
        return (
          <div key={m.month} className="flex-1 min-w-0 h-full flex flex-col items-center"
            title={`${monthLabel(m.month)}: ${m.lancadas} de ${m.esperadas} lançada(s), ${m.sem_frequencia} sem chamada, ${m.pendente} não registrada(s)`}>
            <div className="flex-1 w-full flex flex-col justify-end">
              <div className="w-full bg-[#E83A3C]/80 rounded-t-sm" style={{ height: altura(m.pendente) }} />
              <div className="w-full bg-[#F9B047]/80" style={{ height: altura(m.sem_frequencia) }} />
              <div className="w-full bg-[#13983D]/80 rounded-b-sm" style={{ height: altura(m.lancadas) }} />
            </div>
            <span className={`mt-1 text-[11px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{monthLabel(m.month)}</span>
            <span className={`text-[11px] font-medium ${rateColor(m.fill_rate)}`}>{m.fill_rate == null ? '—' : `${Math.round(m.fill_rate)}%`}</span>
          </div>
        );
      })}
    </div>
  );
}

function Legenda({ darkMode }) {
  return (
    <div className={`flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
      {['ok', 'sem_frequencia', 'pendente'].map((s) => (
        <span key={s} className="inline-flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${STATUS_META[s].dot}`} />
          {s === 'ok' ? 'Lançadas' : STATUS_META[s].label}
        </span>
      ))}
    </div>
  );
}

function ProfessorPainel() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  const [mes, setMes] = useState(mesAtual());
  const [anoInteiro, setAnoInteiro] = useState(false);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const params = anoInteiro
        ? { periodo: 'ano', school_year: mes.slice(0, 4) }
        : { month: mes };
      setDados(await apiGet(`/teachers/${id}/acompanhamento/`, params));
    } catch (err) {
      setErro(err?.message || 'Não foi possível carregar o painel do professor.');
    } finally {
      setCarregando(false);
    }
  }, [id, mes, anoInteiro]);

  useEffect(() => { carregar(); }, [carregar]);

  const forte = darkMode ? 'text-slate-100' : 'text-slate-800';
  const suave = darkMode ? 'text-slate-400' : 'text-slate-500';
  const linha = darkMode ? 'border-slate-700/60' : 'border-slate-100';
  const cabecalho = `text-left text-[11px] font-semibold uppercase tracking-wide ${suave}`;
  const campo = `px-3 py-2 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${darkMode
    ? 'bg-slate-800 border-slate-700 text-slate-100'
    : 'bg-white border-slate-200 text-slate-700'}`;
  const botaoLink = 'text-xs font-medium text-[#0974BA] hover:underline whitespace-nowrap';

  if (carregando && !dados) {
    return <LoadingSpinnerAdvanced message="Carregando o painel do professor..." />;
  }

  const resumo = dados?.resumo;
  // O que está na tela segue o período da resposta, não o do seletor: ao
  // marcar "Ano inteiro", os dados do mês ficam até a resposta do ano chegar.
  const ehAno = dados?.periodo?.tipo === 'ano';
  const rotulo = dados?.periodo?.rotulo || mes;
  const periodoTexto = ehAno ? `ano de ${rotulo}` : `${monthLabel(rotulo)}/${rotulo.slice(0, 4)}`;
  const turmasSemGrade = (dados?.turmas || []).filter((t) => !t.tem_grade);
  const ultima = dados?.ultima_atividade || {};

  return (
    <>
      <header className="flex flex-wrap gap-4 justify-between items-start mb-6">
        <div className="min-w-0">
          <button type="button" onClick={() => navigate('/professores')} className="text-sm text-[#0974BA] hover:underline">
            ← Professores
          </button>
          <h1 className={`mt-1 text-2xl md:text-3xl font-medium ${forte}`}>
            {dados?.teacher?.teacher_name || 'Professor'}
          </h1>
          <p className={`mt-1 text-sm ${suave}`}>
            O que foi lançado no diário — {periodoTexto}
            {dados?.turmas?.length ? ` · ${dados.turmas.length} turma(s): ${dados.turmas.map((t) => t.class_name).join(', ')}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input type="month" value={mes} onChange={(e) => e.target.value && setMes(e.target.value)}
            className={campo} aria-label="Mês" />
          <label className={`inline-flex items-center gap-2 text-sm ${suave} cursor-pointer select-none`}>
            <input type="checkbox" checked={anoInteiro} onChange={(e) => setAnoInteiro(e.target.checked)}
              className="accent-[#0974BA]" />
            Ano inteiro
          </label>
          <RefreshButton onClick={carregar} />
        </div>
      </header>

      {erro && (
        <div className="mb-4 px-4 py-3 rounded-xl text-sm bg-[#E83A3C]/10 text-[#E83A3C] border border-[#E83A3C]/25">{erro}</div>
      )}

      {dados && !dados.turmas.length && (
        <SectionCard darkMode={darkMode} title="Sem turma atribuída"
          subtitle="Este professor não tem turma e disciplina atribuídas nas escolas que você acompanha.">
          <p className={`text-sm ${suave}`}>
            A atribuição é feita em Turmas (disciplina e professor da turma) ou na Grade Horária. Assim que houver,
            as aulas dele aparecem aqui.
          </p>
        </SectionCard>
      )}

      {dados && dados.turmas.length > 0 && (
        <div className="space-y-6">
          {/* 1 + 6: números do período e última atividade */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <KpiCard darkMode={darkMode} label="Aulas previstas" value={resumo.esperadas} tone="nav"
              foot={resumo.futura ? `${resumo.futura} ainda por vir` : 'pela grade horária'} />
            <KpiCard darkMode={darkMode} label="Lançadas" value={pct(resumo.fill_rate)}
              tone={resumo.fill_rate == null ? 'accent' : resumo.fill_rate >= 90 ? 'good' : resumo.fill_rate >= 75 ? 'warn' : 'crit'}
              foot={`${resumo.lancadas} de ${resumo.esperadas}`} />
            <KpiCard darkMode={darkMode} label="Não registradas" value={resumo.pendente} tone="crit"
              foot="aula sem registro no diário" />
            <KpiCard darkMode={darkMode} label="Sem chamada" value={resumo.sem_frequencia} tone="warn"
              foot="registrada, falta a frequência" />
            <KpiCard darkMode={darkMode} label="Aulas extras" value={resumo.extra} tone="accent"
              foot={resumo.substituicao ? `${resumo.substituicao} substituição(ões)` : 'fora da grade'} />
            <KpiCard darkMode={darkMode} label="Última aula registrada"
              value={ultima.aula ? haQuanto(ultima.aula.registrada_em) : '—'} tone="nav"
              foot={ultima.aula
                ? `aula de ${diaMes(ultima.aula.lesson_date)} · ${ultima.aula.class_name}${ultima.aula.subject_name ? ` · ${ultima.aula.subject_name}` : ''}`
                : 'nenhuma aula registrada'} />
          </div>
          <p className={`-mt-3 text-xs ${suave}`}>
            Última nota lançada: {ultima.nota
              ? `${dataCurta(ultima.nota.atualizada_em)} (${haQuanto(ultima.nota.atualizada_em)}) · ${ultima.nota.class_name}${ultima.nota.subject_name ? ` · ${ultima.nota.subject_name}` : ''}`
              : 'nenhuma'}
          </p>

          {turmasSemGrade.length > 0 && (
            <div className="px-4 py-3 rounded-xl text-sm bg-[#F9B047]/15 text-[#B87608] border border-[#F9B047]/30">
              Sem aulas previstas pela grade em {periodoTexto} em: {turmasSemGrade.map((t) => t.class_name).join(', ')}.
              Sem grade horária, só contam as aulas que ele mesmo registrar.
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
            {/* 4: período dia a dia / mês a mês */}
            <SectionCard darkMode={darkMode} className="xl:col-span-2"
              title={ehAno ? 'Mês a mês' : 'Dia a dia'}
              subtitle={ehAno ? 'Aulas lançadas, sem chamada e não registradas por mês' : 'Aulas lançadas / previstas em cada dia'}>
              {ehAno
                ? <BarrasDoAno meses={dados.linha_do_tempo} darkMode={darkMode} />
                : <CalendarioDoMes dias={dados.linha_do_tempo} darkMode={darkMode} />}
              <Legenda darkMode={darkMode} />
            </SectionCard>

            {/* 2: por turma e disciplina */}
            <SectionCard darkMode={darkMode} className="xl:col-span-3" title="Por turma e disciplina"
              subtitle="Quem está mais atrasado aparece primeiro">
              {dados.por_turma.length === 0 ? (
                <p className={`text-sm ${suave}`}>Nenhuma aula prevista ou registrada no período.</p>
              ) : (
                <div className="overflow-x-auto -mx-1">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`border-b ${linha}`}>
                        <th className={`${cabecalho} px-1 py-2`}>Turma</th>
                        <th className={`${cabecalho} px-1 py-2`}>Disciplina</th>
                        <th className={`${cabecalho} px-1 py-2 text-right`}>Previstas</th>
                        <th className={`${cabecalho} px-1 py-2 text-right`}>Lançadas</th>
                        <th className={`${cabecalho} px-1 py-2 text-right`}>Sem chamada</th>
                        <th className={`${cabecalho} px-1 py-2 text-right`}>Não reg.</th>
                        <th className="px-1 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {dados.por_turma.map((l) => (
                        <tr key={`${l.id_class}-${l.id_subject}`} className={`border-b last:border-0 ${linha}`}>
                          <td className={`px-1 py-2 ${forte}`}>
                            {l.class_name}
                            <div className={`text-[11px] ${suave}`}>{l.school_name}</div>
                          </td>
                          <td className={`px-1 py-2 ${forte}`}>{l.subject_name || '—'}</td>
                          <td className="px-1 py-2 text-right tabular-nums">{l.summary.esperadas}</td>
                          <td className={`px-1 py-2 text-right tabular-nums font-semibold ${rateColor(l.summary.fill_rate)}`}>
                            {l.summary.lancadas} <span className="font-normal text-[11px]">({pct(l.summary.fill_rate)})</span>
                          </td>
                          <td className="px-1 py-2 text-right tabular-nums text-[#B87608]">{l.summary.sem_frequencia}</td>
                          <td className="px-1 py-2 text-right tabular-nums text-[#E83A3C]">{l.summary.pendente}</td>
                          <td className="px-1 py-2 text-right">
                            <button type="button" className={botaoLink} onClick={() => navigate(`/turmas/${l.id_class}/diario`)}>
                              Abrir diário
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>
          </div>

          {/* 3: aulas pendentes */}
          <SectionCard darkMode={darkMode} title={`Aulas pendentes · ${dados.total_pendentes}`}
            subtitle="Não registradas ou sem chamada, da mais antiga para a mais recente">
            {dados.pendentes.length === 0 ? (
              <p className="text-sm text-[#13983D]">Nenhuma aula pendente no período.</p>
            ) : (
              <>
                <div className="overflow-x-auto -mx-1 max-h-[420px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className={darkMode ? 'bg-slate-800' : 'bg-white'}>
                      <tr className={`border-b ${linha}`}>
                        <th className={`${cabecalho} px-1 py-2`}>Data</th>
                        <th className={`${cabecalho} px-1 py-2`}>Turma</th>
                        <th className={`${cabecalho} px-1 py-2`}>Disciplina</th>
                        <th className={`${cabecalho} px-1 py-2`}>Aula</th>
                        <th className={`${cabecalho} px-1 py-2`}>Situação</th>
                        <th className="px-1 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {dados.pendentes.map((a) => (
                        <tr key={`${a.date}-${a.id_class}-${a.period}-${a.id_subject}`} className={`border-b last:border-0 ${linha}`}>
                          <td className={`px-1 py-2 tabular-nums ${forte}`}>
                            {diaMes(a.date)} <span className={`text-[11px] ${suave}`}>{DIAS_DA_SEMANA[a.weekday]}</span>
                          </td>
                          <td className={`px-1 py-2 ${forte}`}>{a.class_name}</td>
                          <td className={`px-1 py-2 ${forte}`}>{a.subject_name || '—'}</td>
                          <td className={`px-1 py-2 ${suave}`}>
                            {a.period ? `${a.period}ª` : 'extra'}{a.time_label ? ` · ${a.time_label}` : ''}
                          </td>
                          <td className="px-1 py-2"><StatusPill status={a.status} /></td>
                          <td className="px-1 py-2 text-right">
                            <button type="button" className={botaoLink} onClick={() => navigate(`/turmas/${a.id_class}/diario`)}>
                              Abrir diário
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {dados.total_pendentes > dados.pendentes.length && (
                  <p className={`mt-2 text-xs ${suave}`}>
                    Mostrando as {dados.pendentes.length} mais antigas de {dados.total_pendentes}.
                  </p>
                )}
              </>
            )}
          </SectionCard>

          {/* 5: notas e habilidades por bimestre */}
          <SectionCard darkMode={darkMode} title={`Notas e habilidades · ${dados.periodo.school_year}`}
            subtitle="Alunos com lançamento em cada bimestre (no 1º e 2º ano e na Educação Infantil, o Registro de Habilidades)">
            {dados.avaliacoes.length === 0 ? (
              <p className={`text-sm ${suave}`}>Nenhuma turma para avaliar.</p>
            ) : (
              <div className="overflow-x-auto -mx-1">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`border-b ${linha}`}>
                      <th className={`${cabecalho} px-1 py-2`}>Turma</th>
                      <th className={`${cabecalho} px-1 py-2`}>Disciplina</th>
                      <th className={`${cabecalho} px-1 py-2`}>Lançamento</th>
                      {[1, 2, 3, 4].map((b) => (
                        <th key={b} className={`${cabecalho} px-1 py-2 text-center`}>{b}º bim.</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dados.avaliacoes.map((l) => (
                      <tr key={`${l.id_class}-${l.id_subject}`} className={`border-b last:border-0 ${linha}`}>
                        <td className={`px-1 py-2 ${forte}`}>
                          {l.class_name}
                          <div className={`text-[11px] ${suave}`}>{l.alunos} aluno(s)</div>
                        </td>
                        <td className={`px-1 py-2 ${forte}`}>{l.subject_name || 'Todas (regente)'}</td>
                        <td className={`px-1 py-2 ${suave}`}>{l.tipo === 'habilidades' ? 'Habilidades' : 'Notas'}</td>
                        {l.bimestres.map((b) => (
                          <td key={b.bimestre} className="px-1 py-2 text-center tabular-nums">
                            <span className={`font-semibold ${corDoPct(b.pct)}`}>{b.com_lancamento}/{l.alunos}</span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </div>
      )}
    </>
  );
}

export default ProfessorPainel;
